/**
 * Quick Access Navigation - Fallback for difficult cube navigation
 * NWGSV Command System Phase A.1
 */
import { useState } from 'react';
import type { ModuleId, CubeType } from '../types';

interface QuickAccessProps {
  currentCube: CubeType;
  onModuleSelect: (moduleId: ModuleId) => void;
  onCubeChange: (cube: CubeType) => void;
}

const cubeModules: Record<CubeType, Array<{ id: ModuleId; label: string }>> = {
  command: [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'investor', label: 'Investor Center' },
    { id: 'vault', label: 'Vault' },
    { id: 'funnels', label: 'Funnels' },
  ],
  legacy: [
    { id: 'brittany', label: 'Brittany\'s Corner' },
    { id: 'family-vault', label: 'Family Vault' },
    { id: 'legacy-projects', label: 'Legacy Projects' },
    { id: 'education-center', label: 'Education Center' },
  ],
  growth: [
    { id: 'search', label: 'Search' },
    { id: 'product-builder', label: 'Product Builder' },
    { id: 'affiliate-tools', label: 'Affiliate Tools' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'opportunity-radar', label: 'Opportunity Radar' },
  ],
};

export default function QuickAccess({ currentCube, onModuleSelect, onCubeChange }: QuickAccessProps) {
  const [expanded, setExpanded] = useState(false);

  const modules = cubeModules[currentCube];

  return (
    <div style={styles.container}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={styles.toggleButton}
      >
        {expanded ? '✕ CLOSE' : '☰ QUICK ACCESS'}
      </button>

      {expanded && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span style={styles.panelTitle}>QUICK ACCESS</span>
          </div>

          {/* Cube Switcher */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>CUBE:</div>
            <div style={styles.cubeButtons}>
              {(['command', 'legacy', 'growth'] as CubeType[]).map(cube => (
                <button
                  key={cube}
                  onClick={() => {
                    onCubeChange(cube);
                    setExpanded(false);
                  }}
                  style={{
                    ...styles.cubeButton,
                    ...(currentCube === cube ? styles.cubeButtonActive : {})
                  }}
                >
                  {cube.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Module List */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>{currentCube.toUpperCase()} MODULES:</div>
            <div style={styles.moduleList}>
              {modules.map(module => (
                <button
                  key={module.id}
                  onClick={() => {
                    onModuleSelect(module.id);
                    setExpanded(false);
                  }}
                  style={styles.moduleButton}
                >
                  {module.label}
                </button>
              ))}
            </div>
          </div>

          <div style={styles.tip}>
            💡 Tip: Drag the cube left/right to rotate between modules
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    bottom: '30px',
    left: '30px',
    zIndex: 200,
  },
  toggleButton: {
    padding: '12px 20px',
    background: 'rgba(0, 0, 0, 0.95)',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.8)',
    transition: 'all 0.2s',
  },
  panel: {
    position: 'absolute',
    bottom: '60px',
    left: 0,
    width: '300px',
    background: 'rgba(0, 0, 0, 0.98)',
    border: '1px solid rgba(255, 215, 0, 0.5)',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.9)',
  },
  panelHeader: {
    paddingBottom: '15px',
    borderBottom: '1px solid rgba(255, 215, 0, 0.3)',
    marginBottom: '15px',
  },
  panelTitle: {
    color: '#FFD700',
    fontSize: '14px',
    fontWeight: 'bold',
    letterSpacing: '1px',
  },
  section: {
    marginBottom: '20px',
  },
  sectionLabel: {
    color: '#FFD700',
    fontSize: '11px',
    fontWeight: 'bold',
    marginBottom: '10px',
    opacity: 0.7,
    letterSpacing: '0.5px',
  },
  cubeButtons: {
    display: 'flex',
    gap: '8px',
  },
  cubeButton: {
    flex: 1,
    padding: '8px',
    background: 'rgba(255, 215, 0, 0.1)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    color: '#FFD700',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '10px',
    fontWeight: 'bold',
    transition: 'all 0.2s',
  },
  cubeButtonActive: {
    background: '#FFD700',
    color: 'black',
    borderColor: '#FFD700',
  },
  moduleList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  moduleButton: {
    padding: '10px 12px',
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    color: '#FFD700',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    textAlign: 'left',
    transition: 'all 0.2s',
    fontWeight: '500',
  },
  tip: {
    marginTop: '15px',
    padding: '10px',
    background: 'rgba(255, 215, 0, 0.05)',
    borderRadius: '4px',
    fontSize: '11px',
    color: 'rgba(255, 215, 0, 0.7)',
    lineHeight: '1.4',
  },
};
